var _excluded = ["currentPage", "defaultCurrentPage", "hideLastPage", "i18n", "onPageChange", "onPageSizeChange", "pageSize", "pageSizeOptions", "paginationNavigationSize", "popoverPlacement", "responsiveBreakpoint", "totalItems"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import * as React from 'react';
import ContextualMenu from '../contextual_menu';
import IconChevronDown from '../icons/icon_chevron_down';
import PaginationItem from './pagination_item';
import useControlled from '../utils/use_controlled';
import useFocusVisible from '../utils/use_focus_visible';
import propTypes from './prop_types';
import useStyles from './use_styles';
import { useTheme } from '@veneer/theme';
import paginationI18n from './i18n';
import { useDirection } from '../direction';
import { jsx as ___EmotionJSX } from "@emotion/core";
var DEFAULT_PAGE_LINKS_TO_RENDER = 5;
var MINIMAL_PAGINATION_SIZE = 3;
var Pagination = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  var _i18n$pageSizeInforma, _paginationI18n$pageS;
  var {
      currentPage: currentPageProp,
      defaultCurrentPage = 1,
      hideLastPage,
      i18n,
      onPageChange,
      onPageSizeChange,
      pageSize = 20,
      pageSizeOptions,
      paginationNavigationSize = DEFAULT_PAGE_LINKS_TO_RENDER,
      popoverPlacement,
      responsiveBreakpoint: responsiveBreakpointProp,
      totalItems
    } = _ref,
    paginationProps = _objectWithoutProperties(_ref, _excluded);
  var {
    locale
  } = useDirection();
  function getPageSizeInfo(currentPageParam) {
    var start = pageSize * currentPageParam - (pageSize - 1);
    var end = Math.min(currentPageParam * pageSize, totalItems);
    return "".concat(start.toLocaleString(locale), " - ").concat(end.toLocaleString(locale));
  }
  var [currentPage, setCurrentPage] = useControlled({
    controlled: currentPageProp,
    default: defaultCurrentPage
  });
  var [focusVisible, setFocusVisible] = React.useState(false);
  var theme = useTheme();
  var responsiveBreakpoint = responsiveBreakpointProp || theme.layout.xsMax;
  var {
    handleBlurVisible,
    handleFocusVisible,
    ref: focusVisibleRef
  } = useFocusVisible();
  var handleClick = value => {
    setCurrentPage(value);
    if (onPageChange) {
      onPageChange(value);
    }
  };
  var getPage = item => item === 'previous' ? currentPage - 1 : currentPage + 1;
  var lastPage = Math.ceil(totalItems / pageSize) || 1;
  var middlePage = parseInt((paginationNavigationSize / 2).toString(), 10);
  var diffNavSizeToLastPage = lastPage - paginationNavigationSize;
  var diffFirstPage = currentPage - middlePage;
  if (diffFirstPage > diffNavSizeToLastPage) {
    diffFirstPage = diffNavSizeToLastPage + 1;
  }
  var startPage = Math.max(1, diffFirstPage);
  var endPage = Math.min(lastPage, startPage + (paginationNavigationSize - 1));
  var handlePagination = React.useMemo(() => {
    var itemsList = [];
    var firstNavIsBeingShown = false;
    var lastNavIsBeingShown = false;
    var set3DotsArrayBeginning = () => {
      itemsList.shift();
      itemsList.unshift({
        value: 'ellipsis',
        id: 'previousEllipsis'
      });
      itemsList.unshift({
        value: 1,
        id: '1'
      });
    };
    var set3DotsArrayEnding = () => {
      var newItems = [{
        value: 'ellipsis',
        id: 'nextEllipsis'
      }];
      if (!hideLastPage) {
        itemsList.pop();
        newItems.push({
          value: lastPage,
          id: "".concat(lastPage)
        });
      }
      itemsList.push(...newItems);
    };
    var set3DotsAccordingNavsBeingShown = () => {
      if (!firstNavIsBeingShown) set3DotsArrayBeginning();
      if (!lastNavIsBeingShown) set3DotsArrayEnding();
    };
    var logic3dots = () => {
      if (paginationNavigationSize >= MINIMAL_PAGINATION_SIZE && itemsList.length >= paginationNavigationSize) {
        set3DotsAccordingNavsBeingShown();
      }
    };
    for (var i = startPage; i <= endPage; i += 1) {
      itemsList.push({
        value: i,
        id: "".concat(i)
      });
      if (i === 1) firstNavIsBeingShown = true;
      if (i === lastPage) lastNavIsBeingShown = true;
    }
    logic3dots();
    itemsList.unshift({
      value: 'previous',
      id: 'previous'
    });
    itemsList.push({
      value: 'next',
      id: 'next'
    });
    return {
      itemsList
    };
  }, [endPage, hideLastPage, lastPage, paginationNavigationSize, startPage]);
  var renderNavItem = item => {
    return typeof item === 'number' ? ___EmotionJSX(PaginationItem, {
      "aria-current": currentPage === item ? true : undefined,
      i18n: i18n
      /* eslint-disable-next-line react/jsx-no-bind */,
      onClick: () => handleClick(item),
      page: item.toLocaleString(locale),
      selected: currentPage === item,
      type: "page"
    }) : ___EmotionJSX(PaginationItem, {
      disabled: item !== 'ellipsis' && item === 'next' ? currentPage >= lastPage : currentPage <= 1,
      i18n: i18n
      /* eslint-disable-next-line react/jsx-no-bind */,
      onClick: () => handleClick(getPage(item)),
      type: item
    });
  };
  var hasPageSizeOptions = pageSizeOptions && pageSizeOptions.length > 0;
  var style = useStyles({
    responsiveBreakpoint
  });
  var paginationNav = ___EmotionJSX("nav", _extends({
    "aria-label": (i18n === null || i18n === void 0 ? void 0 : i18n.paginationNavigation) || paginationI18n.paginationNavigation,
    css: style.navigation,
    ref: ref
  }, paginationProps), ___EmotionJSX("ul", null, handlePagination.itemsList.map(itemNav => ___EmotionJSX("li", {
    key: "".concat(itemNav.id)
  }, renderNavItem(itemNav.value)))));
  var handleFocus = React.useCallback(event => {
    if (handleFocusVisible(event)) {
      setFocusVisible(true);
    }
  }, [handleFocusVisible]);
  var handleBlur = React.useCallback(event => {
    if (focusVisible) {
      handleBlurVisible(event);
      setFocusVisible(false);
    }
  }, [focusVisible, handleBlurVisible]);
  var handlePageSize = React.useCallback((event, option) => {
    onPageChange === null || onPageChange === void 0 || onPageChange(1);
    onPageSizeChange === null || onPageSizeChange === void 0 || onPageSizeChange(event, option);
  }, [onPageChange, onPageSizeChange]);
  return ___EmotionJSX(React.Fragment, null, hasPageSizeOptions ? ___EmotionJSX("div", {
    css: style.container
  }, ___EmotionJSX("div", {
    css: style.pageSizeSelect
  }, ___EmotionJSX(ContextualMenu, {
    useJsPositioning: true,
    onClick: handlePageSize,
    options: pageSizeOptions,
    placement: popoverPlacement,
    selectedValue: pageSize
  }, ___EmotionJSX("button", {
    type: "button",
    "aria-label": (i18n === null || i18n === void 0 ? void 0 : i18n.selectPageSize) || paginationI18n.selectPageSize,
    "data-focused": focusVisible ? true : undefined,
    onBlur: handleBlur,
    onFocus: handleFocus,
    ref: focusVisibleRef
  }, getPageSizeInfo(currentPage), ___EmotionJSX(IconChevronDown, null))), ___EmotionJSX("span", null, (i18n === null || i18n === void 0 || (_i18n$pageSizeInforma = i18n.pageSizeInformation) === null || _i18n$pageSizeInforma === void 0 ? void 0 : _i18n$pageSizeInforma.replace('%{total}', totalItems.toLocaleString(locale))) || ((_paginationI18n$pageS = paginationI18n.pageSizeInformation) === null || _paginationI18n$pageS === void 0 ? void 0 : _paginationI18n$pageS.replace('%{total}', totalItems.toLocaleString(locale))))), ___EmotionJSX("div", {
    css: style.paginationContainer
  }, paginationNav)) : paginationNav);
});
Pagination.propTypes = propTypes;
Pagination.displayName = 'Pagination';
export default Pagination;